import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Ellipsis, Eye, HandCoins, Mail, Plus } from 'lucide-react';
import { useState } from 'react';
import type { PrestationParticipationFormatted } from '@/api/prestations/prestations.types';
import { Badge, BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { PopCommands } from '@/components/PopCommands';
import { EntityLink } from '@/components/StyledLink';
import { createSelectColumn } from '@/components/table/select-column';
import type { BadgeOption } from '@/components/table/table.types';
import { formatDate, formatDateTime } from '@/utils/date.utils';
import { useCommiteeSlugNavigate } from '@/utils/router';
import { DebtOrCreanceAmount } from '../../components/DebtOrCreanceAmount';
import { EmployeeNameCell } from '../../components/EmployeeNameCell';
import { EMPLOYEE_STATUS_OPTIONS, getEmployeeStatusDisplay } from '../../employees.constants';
import { DebtRemindersModal } from '../components/DebtRemindersModal';
import { SettleCreancesModal } from '../components/SettleCreancesModal';

const columnHelper = createColumnHelper<PrestationParticipationFormatted>();

type DebtType = 'dette' | 'creance';

const DEBT_TYPE_DISPLAY: Record<DebtType, { label: string; color: BadgeColor }> = {
  dette: { label: 'Dette', color: BadgeColor.RED },
  creance: { label: 'Créance', color: BadgeColor.GREEN },
};

const DEBT_TYPE_OPTIONS: BadgeOption<DebtType>[] = (
  Object.entries(DEBT_TYPE_DISPLAY) as [DebtType, { label: string; color: BadgeColor }][]
).map(([value, display]) => ({ value, ...display }));

export const debtsColumns = [
  columnHelper.accessor((row) => row.employee?.beneficiary.lastName, {
    id: 'lastName',
    header: 'Nom',
    cell: ({ row }) => <EmployeeNameCell participation={row.original} />,
    meta: { filterType: 'text' },
  }),
  columnHelper.accessor((row) => row.employee?.beneficiary.firstName, {
    id: 'firstName',
    header: 'Prénom',
    meta: { filterType: 'text' },
  }),
  columnHelper.accessor((row) => row.employee?.status, {
    id: 'employeeStatus',
    header: 'Statut',
    cell: (info) => {
      const display = getEmployeeStatusDisplay(info.getValue());
      if (!display) return <span className="text-gray-400">-</span>;
      return <Badge color={display.color}>{display.label}</Badge>;
    },
    meta: { filterType: 'badge-enum', options: EMPLOYEE_STATUS_OPTIONS },
  }),
  columnHelper.accessor((row) => row.employee?.matricule, {
    id: 'matricule',
    header: 'Matricule',
    meta: { filterType: 'text' },
  }),
  columnHelper.accessor((row) => row.remainingDebt.abs().toNumber(), {
    id: 'remainingDebt',
    header: 'Montant de la dette/créance',
    cell: (info) => <DebtOrCreanceAmount amount={info.row.original.remainingDebt} />,
    meta: { filterType: 'numeric' },
  }),
] as ColumnDef<PrestationParticipationFormatted>[];

export const debtsExtendedColumns = [
  createSelectColumn<PrestationParticipationFormatted>(),
  ...debtsColumns,
  columnHelper.accessor((row): DebtType => (row.remainingDebt.lt(0) ? 'creance' : 'dette'), {
    id: 'debtType',
    header: 'Type',
    cell: (info) => {
      const { label, color } = DEBT_TYPE_DISPLAY[info.getValue()];
      return <Badge color={color}>{label}</Badge>;
    },
    meta: { filterType: 'badge-enum', options: DEBT_TYPE_OPTIONS },
  }),
  columnHelper.accessor((row) => row.prestation.name, {
    header: 'Intitulé',
    cell: ({ row, getValue }) => (
      <EntityLink entity="prestation" id={row.original.prestationId}>
        {getValue()}
      </EntityLink>
    ),
    meta: { filterType: 'text' },
  }),
  columnHelper.accessor((row) => row.date, {
    id: 'debtDate',
    header: 'Date de la dette',
    cell: (info) => formatDate(info.getValue()),
    meta: { filterType: 'numeric' },
  }),
  columnHelper.accessor((row) => row.debtReminders[0]?.communication?.dateTime, {
    id: 'lastReminderDate',
    header: 'Date dernière relance',
    cell: (info) => formatDateTime(info.getValue()),
    meta: { filterType: 'numeric' },
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => <Actions prestationParticipation={row.original} />,
    size: 50,
  }),
] as ColumnDef<PrestationParticipationFormatted>[];

interface ActionsProps {
  prestationParticipation: PrestationParticipationFormatted;
}

function Actions(props: ActionsProps) {
  const { prestationParticipation } = props;
  const navigate = useCommiteeSlugNavigate();
  const [open, setOpen] = useState(false);
  const isCreance = prestationParticipation.remainingDebt.lt(0);

  const viewEmployee = {
    key: 'view-employee',
    label: 'Voir salarié',
    icon: Eye,
    onClick: () => {
      navigate({
        to: '/$commiteeSlug/beneficiaries/employees/{-$id}',
        params: { id: prestationParticipation.employeeId.toString() },
      });
    },
  };

  if (isCreance) {
    return (
      <>
        {open && (
          <SettleCreancesModal
            closeModal={() => setOpen(false)}
            participationIds={[prestationParticipation.id]}
            total={prestationParticipation.remainingDebt.neg()}
            disbursementAccounts={[prestationParticipation.prestation.disbursementAccount]}
          />
        )}
        <PopCommands
          options={[
            {
              key: 'settle-creance',
              label: <span className="text-purple-600">Régler la créance</span>,
              icon: HandCoins,
              onClick: () => setOpen(true),
            },
            viewEmployee,
          ]}
          trigger={(trigger) => (
            <Button onClick={trigger}>
              <Ellipsis className="w-4 h-4" />
            </Button>
          )}
        />
      </>
    );
  }

  return (
    <>
      <DebtRemindersModal
        open={open}
        closeModal={() => setOpen(false)}
        participationIds={[prestationParticipation.id]}
        employeeCount={1}
        total={prestationParticipation.remainingDebt}
        rows={[prestationParticipation]}
      />
      <PopCommands
        options={[
          {
            key: 'register',
            label: <span className="text-purple-600">Enregistrer un règlement</span>,
            icon: Plus,
            onClick: () => {
              navigate({
                to: '/$commiteeSlug/beneficiaries/transactions/create',
                search: {
                  employeeId: prestationParticipation.employeeId,
                  prestationParticipationIds: [prestationParticipation.id],
                },
              });
            },
          },
          viewEmployee,
          {
            key: 'send-reminder',
            label: 'Relancer le salarié',
            icon: Mail,
            onClick: () => {
              setOpen(true);
            },
          },
        ]}
        trigger={(trigger) => (
          <Button onClick={trigger}>
            <Ellipsis className="w-4 h-4" />
          </Button>
        )}
      />
    </>
  );
}
